<template>
  <v-container fluid class="py-8 bg-surface">
    <v-layout wrap justify-center>
      <v-flex xs12 md11 lg10>
        <v-card class="elevation-0 stylish-card">
          <v-card-title class="headline font-weight-medium pa-6 pb-4 card-header d-flex align-center">
            <v-icon left color="#005f32" size="28" class="mr-3">mdi-cash-multiple</v-icon>
            My Payslips
            <v-spacer></v-spacer>
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              label="Year"
              outlined
              dense
              rounded
              hide-details
              style="max-width: 150px;"
              color="#005f32"
              class="rounded-select"
            ></v-select>
          </v-card-title>
          
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="payslips"
              :loading="loading"
              class="stylish-table"
              disable-sort
              :items-per-page="12"
              hide-default-footer
            >
              <!-- Month/Year Styling -->
              <template v-slot:item.month="{ item }">
                <span class="font-weight-bold text-dark">{{ item.month }}</span>
              </template>
              
              <template v-slot:item.year="{ item }">
                <span class="text-muted">{{ item.year }}</span>
              </template>
              
              <!-- Salary Styling -->
              <template v-slot:item.costToCompany="{ item }">
                <span class="salary-text">₹ {{ Number(item.costToCompany).toLocaleString('en-IN') }}</span>
              </template>

              <!-- Custom Actions -->
              <template v-slot:item.actions="{ item }">
                <div class="d-flex justify-end align-center">
                  <v-btn 
                    text 
                    color="#0ea5e9" 
                    class="action-btn mr-2" 
                    @click="previewPayslip(item)"
                  >
                    <v-icon left size="18">mdi-eye-outline</v-icon>
                    Preview
                  </v-btn>
                  <v-btn 
                    depressed 
                    color="#005f32" 
                    dark 
                    class="action-btn action-btn--solid" 
                    @click="downloadPayslip(item)"
                  >
                    <v-icon left size="18">mdi-download-outline</v-icon>
                    Download
                  </v-btn>
                </div>
              </template>

              <!-- Loading State -->
              <template v-slot:progress>
                <v-progress-linear color="#005f32" indeterminate></v-progress-linear>
              </template>
            </v-data-table>
            <v-layout wrap justify-center v-if="pages > 1">
              <v-flex align-self-center>
                <div class="pt-2 pb-4">
                  <v-pagination prev-icon="mdi-menu-left" next-icon="mdi-menu-right" :length="pages" :total-visible="7"
                    v-model="currentPage" color="#005f32"></v-pagination>
                </div>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- PDF Preview Modal -->
    <v-dialog v-model="previewDialog" max-width="850px" transition="dialog-bottom-transition">
      <v-card class="rounded-lg">
        <v-card-title class="pa-4 bg-light">
          <span class="subtitle-1 font-weight-bold" style="color: #1f2d24;">
            <v-icon left color="#005f32" size="24">mdi-file-pdf-box</v-icon>
            Payslip Preview
          </span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="previewDialog = false" color="#546e7a">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <div v-if="pdfUrl" style="height: 70vh; width: 100%;">
            <object :data="pdfUrl" type="application/pdf" width="100%" height="100%">
              <div class="pa-6 text-center">
                <p>Your browser does not support inline PDFs.</p>
                <v-btn color="#005f32" dark :href="pdfUrl" target="_blank" depressed>
                  Download PDF Instead
                </v-btn>
              </div>
            </object>
          </div>
          <div v-else class="text-center py-12">
            <v-progress-circular indeterminate color="#005f32" size="48" width="4"></v-progress-circular>
            <div class="mt-4 text-muted">Loading your payslip...</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MyPayslips',
  data() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= 2020; i--) {
      years.push(i);
    }
    return {
      selectedYear: currentYear,
      yearOptions: years,
      payslips: [],
      loading: false,
      currentPage: 1,
      pages: 0,
      totalData: 0,
      headers: [
        { text: 'Month', value: 'month', align: 'left', width: '20%' },
        { text: 'Year', value: 'year', align: 'left', width: '20%' },
        { text: 'Monthly CTC', value: 'costToCompany', align: 'left', width: '30%' },
        { text: 'Actions', value: 'actions', align: 'right', width: '30%' }
      ],
      previewDialog: false,
      pdfUrl: null,
    };
  },
  watch: {
    currentPage() {
      this.fetchPayslips();
    },
    selectedYear() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      } else {
        this.fetchPayslips();
      }
    }
  },
  mounted() {
    this.fetchPayslips();
  },
  methods: {
    async fetchPayslips() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: 12
        };
        if (this.selectedYear) {
          params.year = this.selectedYear;
        }
        
        const response = await axios.get(`/employee/payslips`, {
          params,
          headers: {
            'x-auth-token': localStorage.getItem("token")
          }
        });
        if (response.data.status) {
          this.payslips = response.data.data;
          this.totalData = response.data.total;
          this.pages = Math.ceil(this.totalData / response.data.limit);
        }
      } catch (error) {
        console.error('Error fetching payslips:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchPdfBlob(id) {
      const response = await axios.get(`/employee/payslips/${id}/pdf`, {
        headers: {
          'x-auth-token': localStorage.getItem("token")
        },
        responseType: 'blob'
      });
      return new Blob([response.data], { type: 'application/pdf' });
    },
    async previewPayslip(item) {
      this.pdfUrl = null;
      this.previewDialog = true;
      try {
        const blob = await this.fetchPdfBlob(item._id);
        this.pdfUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error previewing payslip:', error);
        this.previewDialog = false;
      }
    },
    async downloadPayslip(item) {
      try {
        const blob = await this.fetchPdfBlob(item._id);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Payslip_${item.month}_${item.year}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading payslip:', error);
      }
    }
  }
};
</script>

<style scoped>
.bg-surface {
  background-color: #f8fafc;
  min-height: calc(100vh - 64px);
}

.stylish-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03) !important;
}

.card-header {
  background-color: #ffffff;
  color: #1e293b !important;
  border-bottom: 1px solid #f1f5f9;
  letter-spacing: -0.01em;
}

.bg-light {
  background-color: #f8fafc;
}

.text-dark {
  color: #334155;
  font-family: kumbhMedium, sans-serif;
}

.text-muted {
  color: #64748b;
  font-family: kumbhRegular, sans-serif;
}

.salary-text {
  color: #0f172a;
  font-family: kumbhMedium, sans-serif;
  font-size: 15px;
  background-color: #f1f5f9;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-block;
}

.stylish-table >>> th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-family: kumbhMedium, sans-serif;
  font-size: 13px !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e2e8f0 !important;
  height: 48px !important;
}

.stylish-table >>> td {
  border-bottom: 1px solid #f1f5f9 !important;
  height: 64px !important;
  transition: background-color 0.2s ease;
}

.stylish-table >>> tbody tr:hover {
  background-color: #f8fafc !important;
}

.action-btn {
  border-radius: 8px;
  text-transform: capitalize;
  font-family: kumbhMedium, sans-serif;
  letter-spacing: 0;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn--solid {
  box-shadow: 0 2px 8px rgba(0, 95, 50, 0.2) !important;
}

.action-btn--solid:hover {
  box-shadow: 0 4px 12px rgba(0, 95, 50, 0.3) !important;
}

.rounded-select >>> .v-input__control > .v-input__slot,
.rounded-select >>> .v-input__control > .v-input__slot fieldset {
  border-radius: 24px !important;
}
</style>
